import { z } from "zod";
import { zGuard } from "../zod/utils.js";

import { assertNever } from "../errors.js";

import { isHistogram, isPie } from "./exploreChartUtils.js";
import { getFieldActiveScaleType } from "./exploreFieldUtils.js";
import {
  ExploreChartConfig,
  ExploreField,
  ExploreVisualizationType,
} from "./types.js";

import { CHART_FOLD_KEYS } from "./index.js";

export type ExploreFieldMenuItem =
  | "aggregation"
  | "truncUnit"
  | "sort"
  | "sortAllRows"
  | "measureSortIndicator"
  | "displayFormat"
  | "scaleType"
  | "filter"
  | "menuDivider"
  | "weekStart"
  | "editCalc"
  | "deleteCalc"
  | "editAsFormula"
  | "facet"
  | "lump"
  | "normalize"
  | "bin"
  | "binType"
  | "label";

export function getExploreFieldMenuItems(
  field: ExploreField | undefined,
  vizType: ExploreVisualizationType,
  chartConfig: ExploreChartConfig,
  hasCustomTimeUnits: boolean,
  isCalc: boolean,
  pivotTotalsEnabled = false,
  pivotSubtotalsEnabled = false,
): ExploreFieldMenuItem[] {
  const configFields: ExploreFieldMenuItem[] = [];
  if (!field) {
    return configFields;
  }
  const activeScaleType = getFieldActiveScaleType(field);

  const showCustomTimeUnits =
    ["DATE", "TIMESTAMPTZ", "TIMESTAMP"].includes(field.dataType) &&
    hasCustomTimeUnits;

  // Can always edit or delete calc columns
  isCalc && configFields.push("editCalc");
  isCalc && configFields.push("deleteCalc");
  if (vizType === "pivot-table") {
    if (
      field.fieldType !== "MEASURE" &&
      zGuard(AggregableChannel, field.channel)
    ) {
      configFields.push("aggregation");
      if (!isCalc) {
        configFields.push("editAsFormula");
      }
    }
    if (
      zGuard(TruncationChannel, field.channel) &&
      (activeScaleType === "datetime" || showCustomTimeUnits)
    ) {
      configFields.push("truncUnit");
      if (field.truncUnit === "week") {
        configFields.push("weekStart");
      }
    }

    if (field.channel === "value" && pivotSubtotalsEnabled) {
      configFields.push("sortAllRows");
    }
    if (
      zGuard(DisplayFormatChannel, field.channel) &&
      activeScaleType !== "string"
    ) {
      configFields.push("displayFormat");
    }
    if (
      pivotTotalsEnabled &&
      zGuard(SortChannel, field.channel) &&
      field.fieldType !== "MEASURE" &&
      field.aggregation == null
    ) {
      if (configFields.length > 0) {
        configFields.push("menuDivider");
      }
      // Add measure sort indicator if this field is sorted by a measure
      if (
        pivotSubtotalsEnabled &&
        (field.sort?.mode === "measure-ascending" ||
          field.sort?.mode === "measure-descending") &&
        field.sort?.measureOrder?.measureId
      ) {
        configFields.push("measureSortIndicator");
      }
      configFields.push("sort");
    }

    if (zGuard(LumpChannel, field.channel) && activeScaleType === "string") {
      configFields.push("lump");
    }
  } else if (vizType === "chart") {
    if (
      field.fieldType !== "MEASURE" &&
      zGuard(AggregableChannel, field.channel) &&
      field.value !== CHART_FOLD_KEYS &&
      !(isPie(chartConfig) && field.channel === "color")
    ) {
      configFields.push("aggregation");
      if (!isCalc) {
        configFields.push("editAsFormula");
      }
    }

    if (
      zGuard(TruncationChannel, field.channel) &&
      ((activeScaleType === "datetime" &&
        !(
          isPie(chartConfig) && ["color", "cross-axis"].includes(field.channel)
        )) ||
        showCustomTimeUnits) &&
      !(isHistogram(chartConfig) && field.channel === "base-axis")
    ) {
      configFields.push("truncUnit");
      if (field.truncUnit === "week") {
        configFields.push("weekStart");
      }
    }
    if (
      zGuard(DisplayFormatChannel, field.channel) &&
      activeScaleType !== "string"
    ) {
      configFields.push("displayFormat");
    }
    if (
      zGuard(ScaleTypeChannel, field.channel) &&
      field.fieldType !== "MEASURE" &&
      field.value !== CHART_FOLD_KEYS &&
      field.aggregation == null &&
      !(
        isPie(chartConfig) && ["color", "cross-axis"].includes(field.channel)
      ) &&
      !(isHistogram(chartConfig) && field.channel === "base-axis")
    ) {
      configFields.push("scaleType");
    }
    if (
      zGuard(SortChannel, field.channel) &&
      activeScaleType === "string" &&
      field.fieldType !== "MEASURE" &&
      field.aggregation == null
    ) {
      if (configFields.length > 0) {
        configFields.push("menuDivider");
      }
      configFields.push("sort");
    }

    if (
      zGuard(LumpChannel, field.channel) &&
      activeScaleType === "string" &&
      field.value !== CHART_FOLD_KEYS
    ) {
      configFields.push("lump");
    }
  } else if (vizType === "sankey") {
    // Sankey menu config
    if (
      field.fieldType !== "MEASURE" &&
      zGuard(AggregableChannel, field.channel)
    ) {
      configFields.push("aggregation");
      if (!isCalc) {
        configFields.push("editAsFormula");
      }
    }
    // Add sorting options for column channels in sankey (columns are never measures and don't have aggregations)
    if (field.channel === "column") {
      if (configFields.length > 0) {
        configFields.push("menuDivider");
      }
      configFields.push("sort");
    }
    // Add lump (top-N) for column channels in sankey with string scale type
    if (field.channel === "column" && activeScaleType === "string") {
      configFields.push("lump");
    }
  } else {
    assertNever(vizType, vizType);
  }

  if (field.value !== CHART_FOLD_KEYS) {
    if (configFields.length > 0 && !configFields.includes("lump")) {
      configFields.push("menuDivider");
    }
    configFields.push("filter");
  }

  if (field.channel === "h-facet" || field.channel === "v-facet") {
    if (configFields.length > 0) {
      configFields.push("menuDivider");
    }
    configFields.push("facet");
    configFields.push("bin");
  }

  if (isHistogram(chartConfig) && field.channel === "base-axis") {
    if (configFields.length > 0) {
      configFields.push("menuDivider");
    }
    configFields.push("normalize");
    configFields.push("binType");
    configFields.push("bin");
  }

  configFields.push("label");
  return configFields;
}

export const TruncationChannel = z.enum([
  "row",
  "column",
  "base-axis",
  "h-facet",
  "v-facet",
  "source",
  "destination", // "color", // add later // "tooltip" // add later
]);

export const ScaleTypeChannel = z.enum(["base-axis", "cross-axis", "color"]);

export const SortChannel = z.enum([
  "base-axis",
  "h-facet",
  "v-facet",
  "color",
  "row",
  "column",
  "source",
  "destination",
]);

export const DisplayFormatChannel = z.enum([
  "value",
  "row",
  "column",
  "base-axis",
  "cross-axis",
  "color",
  "tooltip",
  "source",
  "destination",
]);

export const LumpChannel = z.enum([
  "base-axis",
  "color",
  "h-facet",
  "v-facet",
  "row",
  "column",
  "source",
  "destination",
]);

export const AggregableChannel = z.enum([
  "value",
  "cross-axis",
  "color",
  "tooltip",
]);

export const AggregationRequiredChannel = z.enum(["value"]);

export const FoldKeysChannel = z.enum([
  "base-axis",
  "color",
  "h-facet",
  "v-facet",
  "tooltip",
]);

export const BinChannel = z.enum(["base-axis", "h-facet", "v-facet"]);
